import supabase from '../../utils/supabaseClient';

/**
 * Fetches slides for uploaded pitch decks directly from the database
 * @param {string} deckId - The UUID of the pitch deck to fetch
 * @returns {Promise<Array>} - Array of slide objects with image URLs or PDF info
 */
export async function getUploadedSlides(deckId) {
  if (!deckId) return [];
  
  try {
    console.log('Fetching uploaded slides for deck:', deckId);
    
    // Check if there's a converted PDF version first
    const { data: deckData, error: deckError } = await supabase
      .from('pitch_decks')
      .select('pdf_url, file_url, file_type, conversion_status')
      .eq('id', deckId)
      .single();
    
    if (deckError) {
      console.error('Error fetching pitch deck data:', deckError);
      return [];
    }
    
    // If PDF is available, create a slide with the PDF viewer
    if (deckData.pdf_url) {
      console.log('Found converted PDF for deck:', deckData.pdf_url);
      return [{
        title: 'Pitch Deck',
        content: '',
        key: 'pdf-slide',
        pdf_url: deckData.pdf_url,
        is_pdf: true,
        images: []
      }];
    }
    
    // If there's a PPTX file but no PDF yet
    if (deckData.file_url && (
        deckData.file_url.toLowerCase().endsWith('.pptx') || 
        deckData.file_url.toLowerCase().endsWith('.ppt')
      )) {
      console.log('Found PPTX file:', deckData.file_url);
      
      return [{
        title: 'Pitch Deck',
        content: 'PowerPoint viewing is no longer supported. For improved viewing experience, please upload your pitch deck in PDF format.',
        key: 'unsupported-format',
        file_url: deckData.file_url,
        file_type: deckData.file_type || 'pptx',
        images: []
      }];
    }
    
    // If there's a PDF file in file_url
    if (deckData.file_url && deckData.file_url.toLowerCase().endsWith('.pdf')) {
      console.log('Found PDF file in file_url:', deckData.file_url);
      
      return [{
        title: 'Pitch Deck',
        content: '',
        key: 'pdf-slide',
        pdf_url: deckData.file_url,
        is_pdf: true,
        images: []
      }];
    }
    
    // Skip metadata check and go directly to storage bucket
    console.log('Checking storage bucket for uploaded slides');
    
    const { data: files, error: storageError } = await supabase
      .storage
      .from('pitch_deck_assets')
      .list(`decks/${deckId}/`);
    
    if (storageError || !files || files.length === 0) {
      console.log('No files found in pitch_deck_assets bucket:', storageError?.message || 'Empty directory');
      
      // Check in pitch_decks bucket as a fallback
      try {
        // Get user_id for possible matching in filename
        const { data: userData } = await supabase
          .from('pitch_decks')
          .select('user_id')
          .eq('id', deckId)
          .single();
          
        if (userData && userData.user_id) {
          const { data: pptxFiles, error: pptxError } = await supabase
            .storage
            .from('pitch_decks')
            .list('', { 
              search: userData.user_id 
            });
            
          if (!pptxError && pptxFiles && pptxFiles.length > 0) {
            console.log(`Found ${pptxFiles.length} potential files in pitch_decks bucket`);
            
            // Find PPTX files
            const presentationFiles = pptxFiles.filter(file => 
              file.name.toLowerCase().endsWith('.pptx') || 
              file.name.toLowerCase().endsWith('.ppt') || 
              file.name.toLowerCase().endsWith('.pdf')
            );
            
            if (presentationFiles.length > 0) {
              console.log('Found presentation files in pitch_decks bucket');
              
              // Start with the most recently modified file
              presentationFiles.sort((a, b) => 
                new Date(b.created_at || b.lastModified) - new Date(a.created_at || a.lastModified)
              );
              
              const file = presentationFiles[0];
              const fileUrl = supabase.storage
                .from('pitch_decks')
                .getPublicUrl(file.name).data.publicUrl;
              
              // Handle PDF files differently
              if (file.name.toLowerCase().endsWith('.pdf')) {
                console.log('Found PDF file in storage:', file.name);
                return [{
                  title: 'Pitch Deck',
                  content: '',
                  key: 'pdf-slide',
                  pdf_url: fileUrl,
                  is_pdf: true,
                  images: []
                }];
              }
              
              // Handle PowerPoint files
              return [{
                title: 'Pitch Deck',
                content: 'PowerPoint viewing is no longer supported. For improved viewing experience, please upload your pitch deck in PDF format.',
                key: 'presentation-file',
                file_url: fileUrl,
                file_type: file.name.split('.').pop().toLowerCase(),
                needs_conversion: false,
                images: []
              }];
            }
          }
        }
      } catch (bucketError) {
        console.error('Error checking pitch_decks bucket:', bucketError);
      }
            
      return [];
    }
    
    // Convert storage files to URLs and then to slides
    const imageFiles = files.filter(file => 
      file.name.toLowerCase().endsWith('.jpg') || 
      file.name.toLowerCase().endsWith('.jpeg') || 
      file.name.toLowerCase().endsWith('.png') || 
      file.name.toLowerCase().endsWith('.gif')
    );
    
    console.log(`Found ${imageFiles.length} image files in storage`);
    
    if (imageFiles.length === 0) {
      return [];
    }
    
    // Sort files by name (assuming they're named sequentially)
    imageFiles.sort((a, b) => a.name.localeCompare(b.name));
    
    // Create public URLs for each file
    const slides = imageFiles.map((file, index) => {
      const imageUrl = supabase.storage
        .from('pitch_deck_assets')
        .getPublicUrl(`decks/${deckId}/${file.name}`).data.publicUrl;
      
      return {
        title: `Slide ${index + 1}`,
        content: '',
        key: `uploaded-slide-${index + 1}`,
        images: [{
          url: imageUrl,
          type: 'uploaded',
          slide_number: index + 1
        }]
      };
    });
    
    console.log(`Created ${slides.length} slides from storage files`);
    return slides;
  } catch (error) {
    console.error('Error in getUploadedSlides:', error);
    return [];
  }
} 